import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router";
import Navbar from "../../../../globals/components/Navbar";
import { apiClient } from "../../../../globals/utils/api";
import dayjs from "dayjs";
import {
  TextField,
  Checkbox,
  FormControlLabel,
  Button,
  Box,
  Container,
  Grid,
  Typography,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Alert,
  MenuItem,
  Switch,
} from "@mui/material";
import { toast } from "react-toastify";
import InputMask from "react-input-mask";
import { EMAIL_DEBOUNCE_TIMEOUT } from "../../../../globals/utils/constants";
import {
  validateAddressFields,
  formatCanonicalAddress,
} from "../../../../globals/utils/addressValidation";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import debounce from "lodash.debounce";
import AddressSubForm from "../../../../globals/components/AddressSubForm";

function CreateProfessionalGuardian() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sendInvite, setSendInvite] = useState(false);
  const [formValues, setFormValues] = useState({
    full_name: "",
    preferred_name: "",
    business_name: "",
    mailing_address: {
      street_address: "",
      city: "",
      state: "",
      zipCode: "",
    },
    physical_address: {
      street_address: "",
      city: "",
      state: "",
      zipCode: "",
    },
    email_address: "",
    cell_phone: null,
    date_of_birth: "",
    date_of_first_bond: "",
    oppg_number: "",
    ams_customer_number: "",
    professional_fiduciary_questionnaire_last_completion_date: "",
    general_indemnity_agreement_on_file: false,
    letter_of_authority: false,
    permission_to_text: false,
    preferred_billing: null,
  });
  
  // Local contacts state (before API creation)
  const [localContacts, setLocalContacts] = useState([]);
  const [isCreatingContacts, setIsCreatingContacts] = useState(false);
  const [showResetButton, setShowResetButton] = useState(false);
  
  // PG Contacts state (after API creation)
  const [pgContacts, setPgContacts] = useState([]);
  const [contactDialogOpen, setContactDialogOpen] = useState(false);
  const [editingContactIndex, setEditingContactIndex] = useState(null);
  const [contactForm, setContactForm] = useState({
    full_name: "",
    email: "",
    phone_number: "",
  });
  const [contactEmailExists, setContactEmailExists] = useState(false);
  const [contactEmailChecking, setContactEmailChecking] = useState(false);
  const [contactEmailError, setContactEmailError] = useState("");
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [deletingContact, setDeletingContact] = useState(null);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  
  const [emailExists, setEmailExists] = useState(false);
  const [emailChecking, setEmailChecking] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [emailError, setEmailError] = useState("");

  // Field-level validation errors keyed by qualified field name, e.g.
  // "mailing_address.street_address". Populated by handleSubmit and cleared
  // as the user edits the offending field. Used to drive `error` /
  // `helperText` props on the address inputs so the user immediately sees
  // which field is wrong, instead of guessing why the submit silently failed.
  const [fieldErrors, setFieldErrors] = useState({});

  // When checked, the Physical Address sub-form is hidden and on save BOTH
  // `mailing_address` and `physical_address` columns receive the SAME
  // canonical 4-part string. Defaults to false on Create.
  const [addressesAreSame, setAddressesAreSame] = useState(false);

  // Load form data and contacts from localStorage on component mount
  useEffect(() => {
    // Load contacts
    const savedContacts = localStorage.getItem('pgLocalContacts');
    if (savedContacts) {
      try {
        const parsedContacts = JSON.parse(savedContacts);
        setLocalContacts(parsedContacts);
      } catch (error) {
        console.error('Error loading contacts from localStorage:', error);
      }
    }

    // Load professional guardian form data
    const savedFormData = localStorage.getItem('pgFormData');
    if (savedFormData) {
      try {
        const parsedFormData = JSON.parse(savedFormData);
        setFormValues(parsedFormData);
      } catch (error) {
        console.error('Error loading form data from localStorage:', error);
      }
    }

    // Show reset button if there's any saved data
    const hasContacts = savedContacts && JSON.parse(savedContacts).length > 0;
    const hasFormData = !!savedFormData;
    setShowResetButton(hasContacts || hasFormData);
  }, []);

  // Save contacts to localStorage whenever localContacts changes
  useEffect(() => {
    if (localContacts.length > 0) {
      localStorage.setItem('pgLocalContacts', JSON.stringify(localContacts));
    } else {
      localStorage.removeItem('pgLocalContacts');
    }
    updateResetButtonVisibility();
  }, [localContacts]);

  // Save form data to localStorage whenever formValues changes
  useEffect(() => {
    const hasFormData = Object.values(formValues).some(value => {
      if (typeof value === 'object' && value !== null) {
        return Object.values(value).some(v => v !== '' && v !== null && v !== false);
      }
      return value !== '' && value !== null && value !== false;
    });

    if (hasFormData) {
      localStorage.setItem('pgFormData', JSON.stringify(formValues));
    } else {
      localStorage.removeItem('pgFormData');
    }
    updateResetButtonVisibility();
  }, [formValues]);

  // Helper function to update reset button visibility
  const updateResetButtonVisibility = () => {
    const savedContacts = localStorage.getItem('pgLocalContacts');
    const savedFormData = localStorage.getItem('pgFormData');
    
    const hasContacts = savedContacts && JSON.parse(savedContacts).length > 0;
    const hasFormData = !!savedFormData;
    
    setShowResetButton(hasContacts || hasFormData);
  };

  // Email validation function
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Debounced function to check email existence for main form
  const debouncedCheckEmail = useRef(
    debounce(async (email) => {
      if (!email) {
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        setEmailError("");
        return;
      }

      // Validate email format
      if (!validateEmail(email)) {
        setEmailError("Please enter a valid email address");
        setEmailExists(false);
        setEmailChecking(false);
        setEmailDialogOpen(false);
        return;
      }

      setEmailError("");
      setEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setEmailDialogOpen(true);
          setEmailExists(true);
        } else {
          setEmailExists(false);
        }
      } finally {
        setEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT)
  ).current;

  // Debounced function to check email existence for contacts
  const debouncedCheckContactEmail = useRef(
    debounce(async (email) => {
      if (!email) {
        setContactEmailExists(false);
        setContactEmailChecking(false);
        setContactEmailError("");
        return;
      }

      // Validate email format
      if (!validateEmail(email)) {
        setContactEmailError("Please enter a valid email address");
        setContactEmailExists(false);
        setContactEmailChecking(false);
        return;
      }

      setContactEmailError("");
      setContactEmailChecking(true);
      try {
        await apiClient("/email-exists", "POST", { email });
        setContactEmailExists(false);
      } catch (e) {
        if (e?.detail === "Email already exists") {
          setContactEmailExists(true);
        } else {
          setContactEmailExists(false);
        }
      } finally {
        setContactEmailChecking(false);
      }
    }, EMAIL_DEBOUNCE_TIMEOUT)
  ).current;

  // PG Contact form handlers
  const handleContactInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (name === "email") {
      debouncedCheckContactEmail(value);
    }
  };

  // Reset all data (form and contacts)
  const resetAllData = () => {
    // Reset form data
    setFormValues({
      full_name: "",
      preferred_name: "",
      business_name: "",
      mailing_address: {
        street_address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      physical_address: {
        street_address: "",
        city: "",
        state: "",
        zipCode: "",
      },
      email_address: "",
      cell_phone: null,
      date_of_birth: "",
      date_of_first_bond: "",
      oppg_number: "",
      ams_customer_number: "",
      professional_fiduciary_questionnaire_last_completion_date: "",
      general_indemnity_agreement_on_file: false,
      letter_of_authority: false,
      permission_to_text: false,
      preferred_billing: null,
    });

    // Reset contacts
    setLocalContacts([]);
    setPgContacts([]);
    setInviteInfo(null);

    // Clear localStorage
    localStorage.removeItem('pgLocalContacts');
    localStorage.removeItem('pgFormData');

    // Reset email states
    setEmailExists(false);
    setEmailChecking(false);
    setEmailDialogOpen(false);
    setEmailError("");

    setShowResetButton(false);
    toast.success("All data cleared successfully!");
  };

  // Create contacts sequentially after PG creation
  const createContactsSequentially = async (pgId) => {
    setIsCreatingContacts(true);
    const results = {};

    for (let i = 0; i < localContacts.length; i++) {
      const contact = localContacts[i];
      try {
        await apiClient("/pg-contacts-invite/", "POST", {
          pg: pgId,
          full_name: contact.full_name,
          email: contact.email,
          phone_number: contact.phone_number,
          send_invite: sendInvite,
        });
        results[i] = { success: true, message: "Created successfully" };
      } catch (error) {
        results[i] = { 
          success: false, 
          message: error.error || error.message || "Failed to create contact" 
        };
        toast.error(`Failed to create contact ${contact.full_name}: ${results[i].message}`);
      }
    }

    setIsCreatingContacts(false);
    
    // Fetch updated contacts list
    try {
      const contactsResponse = await apiClient(`/pg-contacts/pg/${pgId}/`, "GET");
      setPgContacts(contactsResponse.pg_contacts || []);
    } catch (error) {
      console.error("Failed to fetch updated contacts:", error);
    }

    return results;
  };

  const handleAddContact = () => {
    if (!contactForm.full_name || !contactForm.email) {
      toast.error("Full name and email are required");
      return;
    }

    if (contactEmailError || contactEmailChecking) {
      toast.error("Please fix email validation errors");
      return;
    }

    if (editingContactIndex !== null) {
      // Update existing contact in local state
      const updatedContacts = [...localContacts];
      updatedContacts[editingContactIndex] = { ...contactForm };
      setLocalContacts(updatedContacts);
      setEditingContactIndex(null);
      
      // toast.success(`Contact ${contactForm.full_name} updated successfully!`);
    } else {
      // Add new contact to local state
      const newContact = { ...contactForm };
      setLocalContacts(prev => [...prev, newContact]);
      
      // toast.success(`Contact ${contactForm.full_name} added successfully!`);
    }
    
    // Reset form and close dialog
    setContactForm({
      full_name: "",
      email: "",
      phone_number: "",
    });
    setContactDialogOpen(false);
    setContactEmailExists(false);
    setContactEmailError("");
  };

  const handleEditContact = (index) => {
    // Check if we're editing a local contact or an existing PG contact
    if (index < localContacts.length) {
      setContactForm(localContacts[index]);
      setEditingContactIndex(index);
    } else {
      // Adjust index for pgContacts array
      const pgIndex = index - localContacts.length;
      setContactForm(pgContacts[pgIndex]);
      setEditingContactIndex(index);
    }
    setContactDialogOpen(true);
  };

  const handleDeleteContact = (contact, index) => {
    setContactToDelete({ contact, index });
    setDeleteDialogOpen(true);
  };

  const confirmDeleteContact = async () => {
    if (!contactToDelete) return;

    const { contact, index } = contactToDelete;

    // Check if we're deleting a local contact or an existing PG contact
    if (index < localContacts.length) {
      // Delete from local contacts
      setLocalContacts(prev => prev.filter((_, i) => i !== index));
      toast.success(`Contact ${contact.full_name} removed successfully!`);
    } else {
      // Delete from existing PG contacts via API
      setDeletingContact(contact.contact_id || index);

      try {
        // If contact has been invited (has contact_id), use the API to soft delete
        if (contact.contact_id) {
          await apiClient(`/pg-contacts/${contact.contact_id}/delete/`, "DELETE");
          
          // Fetch updated contacts list from the new API endpoint
          if (inviteInfo) {
            const pgId = inviteInfo.pg_id || inviteInfo.pg || inviteInfo.professional_guardian_id || inviteInfo.id;
            if (pgId) {
              const contactsResponse = await apiClient(`/pg-contacts/pg/${pgId}/`, "GET");
              setPgContacts(contactsResponse.pg_contacts || []);
            }
          }
          
          toast.success(`Contact ${contact.full_name} deleted successfully!`);
        } else {
          // If contact hasn't been invited yet, just remove from local state
          setPgContacts(prev => prev.filter((_, i) => i !== (index - localContacts.length)));
          toast.success(`Contact ${contact.full_name} removed successfully!`);
        }
      } catch (error) {
        toast.error(`Failed to delete contact: ${error.error || error.message}`);
      } finally {
        setDeletingContact(null);
      }
    }

    setDeleteDialogOpen(false);
    setContactToDelete(null);
  };



  const openContactDialog = () => {
    setContactForm({
      full_name: "",
      email: "",
      phone_number: "",
    });
    setEditingContactIndex(null);
    setContactEmailExists(false);
    setContactEmailError("");
    setContactDialogOpen(true);
  };

  // Build the canonical "street, city, state, zip" string for the legacy
  // `mailing_address` / `physical_address` DB columns. Returns null when the
  // 4 inputs are not all valid - callers should treat null as "do not write
  // this address" so we never persist garbage like ", , , 33773" again.
  function formatAddress(address) {
    return formatCanonicalAddress(address);
  }

  // Clear any field-level validation error for `key` once the user starts
  // editing the offending field. Keeps the red ring + helperText from
  // sticking around after the user has corrected the value.
  const clearFieldError = (key) => {
    if (!fieldErrors[key]) return;
    setFieldErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  // Generic non-address input change. Address sub-forms own their own state
  // via the AddressSubForm component (see render below).
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormValues({
      ...formValues,
      [name]: type === "checkbox" ? checked : value,
    });
    if (name === "email_address") {
      debouncedCheckEmail(value);
    }
    clearFieldError(name);
  };

  // When the user flips the "addresses are the same" toggle ON, mirror
  // mailing into physical and clear any physical errors so the (now hidden)
  // Physical sub-form can't block submit. When flipped OFF we keep whatever
  // physical values were last entered so the user doesn't lose work.
  const handleAddressesAreSameChange = (checked) => {
    setAddressesAreSame(checked);
    if (checked) {
      setFormValues((prev) => ({
        ...prev,
        physical_address: { ...prev.mailing_address },
      }));
      setFieldErrors((prev) => {
        const next = { ...prev };
        Object.keys(next).forEach((k) => {
          if (k.startsWith("physical_address.")) delete next[k];
        });
        return next;
      });
    }
  };

  const handleSubmit = async () => {
    // Validate email before submission
    if (!validateEmail(formValues.email_address)) {
      setEmailError("Please enter a valid email address");
      return;
    }

    // Validate preferred_billing is explicitly selected
    if (formValues.preferred_billing === null || formValues.preferred_billing === undefined) {
      toast.error("Please select a Preferred Billing Method (Yes or No).");
      return;
    }

    // Validate full name (required to avoid empty PG records).
    if (!formValues.full_name || !formValues.full_name.trim()) {
      toast.error("Full Name is required.");
      setFieldErrors((prev) => ({
        ...prev,
        full_name: "Full Name is required",
      }));
      return;
    }

    // Validate the structured address sub-forms BEFORE we let
    // formatCanonicalAddress() reject them. Mailing is always required;
    // Physical is required only when the toggle is OFF (when ON, we copy
    // mailing into physical at submit time).
    const mailingErrors = validateAddressFields(formValues.mailing_address, {
      requireAll: true,
      fieldPrefix: "mailing_address.",
    });
    const physicalErrors = addressesAreSame
      ? {}
      : validateAddressFields(formValues.physical_address, {
          requireAll: true,
          fieldPrefix: "physical_address.",
        });
    const allAddressErrors = { ...mailingErrors, ...physicalErrors };
    if (Object.keys(allAddressErrors).length > 0) {
      setFieldErrors((prev) => ({ ...prev, ...allAddressErrors }));
      const summary = Object.entries(allAddressErrors)
        .map(([key, msg]) => {
          const which = key.startsWith("mailing_address.")
            ? "Mailing"
            : "Physical";
          return `${which} Address - ${msg}`;
        })
        .join("; ");
      toast.error(`Please fix the address fields: ${summary}`);
      return;
    }

    setLoading(true);

    try {
      // Handle date fields - convert empty strings to null
      const formattedDateOfBirth = formValues.date_of_birth && formValues.date_of_birth.trim() !== ""
        ? dayjs(formValues.date_of_birth).format("YYYY-MM-DD")
        : null;

      const formattedQuestionnaireDate = formValues.professional_fiduciary_questionnaire_last_completion_date && 
        formValues.professional_fiduciary_questionnaire_last_completion_date.trim() !== ""
        ? dayjs(formValues.professional_fiduciary_questionnaire_last_completion_date).format("YYYY-MM-DD")
        : null;

      const formattedDateOfFirstBond = formValues.date_of_first_bond && formValues.date_of_first_bond.trim() !== ""
        ? dayjs(formValues.date_of_first_bond).format("YYYY-MM-DD")
        : null;

      // When the "addresses are the same" toggle is on, send the mailing
      // canonical for both columns so they stay byte-identical in the DB.
      const mailingCanonical = formatAddress(formValues.mailing_address);
      const physicalCanonical = addressesAreSame
        ? mailingCanonical
        : formatAddress(formValues.physical_address);

      const response = await apiClient("/generate-invite-link", "POST", {
        full_name: formValues.full_name,
        email_address: formValues.email_address,
        preferred_name: formValues.preferred_name,
        business_name: formValues.business_name,
        mailing_address: mailingCanonical,
        physical_address: physicalCanonical,
        cell_phone: formValues.cell_phone,
        date_of_birth: formattedDateOfBirth,
        date_of_first_bond: formattedDateOfFirstBond,
        oppg_number: formValues.oppg_number,
        ams_customer_number: formValues.ams_customer_number,
        professional_fiduciary_questionnaire_last_completion_date: formattedQuestionnaireDate,
        general_indemnity_agreement_on_file:
          formValues.general_indemnity_agreement_on_file,
        letter_of_authority: formValues.letter_of_authority,
        permission_to_text: formValues.permission_to_text,
        total_bonds: parseInt(formValues.total_bonds, 10) || 0,
        total_outstanding_bond_amount: parseInt(formValues.total_outstanding_bond_amount, 10) || 0,
        ...(formValues.preferred_billing !== null && formValues.preferred_billing !== undefined
          ? { preferred_billing: formValues.preferred_billing }
          : {}),
        send_invite: sendInvite,
      });

      console.log("Professional Guardian creation response:", response);
      setInviteInfo(response);
      // toast.success("Professional Guardian created successfully!");

      // Create contacts sequentially if there are local contacts
      if (localContacts.length > 0) {
        const pgId = response.pg_id || response.pg || response.professional_guardian_id || response.id;
        if (pgId) {
          await createContactsSequentially(pgId);
          // Clear local contacts after successful creation
          setLocalContacts([]);
        } else {
          toast.error("Professional guardian ID not found. Contacts will not be created.");
        }
      }

      // Clear form data from localStorage after successful creation
      localStorage.removeItem('pgFormData');
      localStorage.removeItem('pgLocalContacts');
      updateResetButtonVisibility();
      toast.success(
        sendInvite
          ? "Professional Guardian and Contacts created and invites sent successfully!"
          : "Professional Guardian and Contacts created successfully!"
      );
      navigate("/vermost-professional-guardian-list");
    } catch (error) {
      toast.error("Failed to create professional guardian. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Dialog handlers
  const handleDialogClose = () => {
    setEmailDialogOpen(false);
  };
  const handleDialogContinue = () => {
    setEmailDialogOpen(false);
    setEmailExists(false);
    // Keep the email as is, allow user to proceed
  };
  const handleDialogCancel = () => {
    setEmailDialogOpen(false);
    setFormValues((prev) => ({ ...prev, email_address: "" }));
    setEmailExists(false);
  };

  return (
    <>
      <Navbar />
      <Container maxWidth="md" sx={{ marginTop: 4 }}>
        <Paper
          sx={{ padding: 4, backgroundColor: "#f5f5f5", borderRadius: "15px" }}
        >
          <Typography
            variant="h4"
            gutterBottom
            sx={{ textAlign: "center", fontWeight: "bold" }}
          >
            Create Professional Guardian
          </Typography>

          {inviteInfo && (
            <Box
              sx={{
                mb: 3,
                p: 2,
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                background: "#f9f9f9",
              }}
            >
              <Typography variant="h6" color="success.main">
                Professional Guardian Created!
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Invite Link:</strong>{" "}
                <a
                  href={inviteInfo.invite_link}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {inviteInfo.invite_link}
                </a>
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Generated Username:</strong>{" "}
                {inviteInfo.generated_username}
              </Typography>
              <Typography variant="body2" sx={{ mt: 1 }}>
                <strong>Generated Password:</strong>{" "}
                {inviteInfo.generated_password}
              </Typography>
              <Button
                sx={{ mt: 2 }}
                variant="outlined"
                onClick={() => {
                  navigator.clipboard.writeText(inviteInfo.invite_link);
                  toast.success("Invite link copied!");
                }}
              >
                Copy Invite Link
              </Button>
            </Box>
          )}

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Full Name"
              name="full_name"
              value={formValues.full_name}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Preferred Name"
              name="preferred_name"
              value={formValues.preferred_name}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Business Name"
              name="business_name"
              value={formValues.business_name}
              onChange={handleInputChange}
            />
          </Grid>

          {/* Same-as toggle - when on, the Physical sub-form is hidden and
              both DB columns receive the same canonical string at submit. */}
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Switch
                  checked={addressesAreSame}
                  onChange={(e) => handleAddressesAreSameChange(e.target.checked)}
                />
              }
              label="Mailing and Physical addresses are the same"
            />
          </Grid>

          {/* Mailing Address (always visible) */}
          <Grid item xs={12}>
            <AddressSubForm
              label="Mailing Address"
              value={formValues.mailing_address}
              onChange={(next) => {
                setFormValues((prev) => ({
                  ...prev,
                  mailing_address: next,
                  // Keep physical mirrored while toggle is on so save still
                  // sends the same canonical string for both.
                  ...(addressesAreSame
                    ? { physical_address: { ...next } }
                    : {}),
                }));
              }}
              errors={fieldErrors}
              setErrors={setFieldErrors}
              fieldPrefix="mailing_address."
            />
          </Grid>

          {/* Physical Address (hidden when toggle is on) */}
          {!addressesAreSame && (
            <Grid item xs={12}>
              <AddressSubForm
                label="Physical Address"
                value={formValues.physical_address}
                onChange={(next) =>
                  setFormValues((prev) => ({ ...prev, physical_address: next }))
                }
                errors={fieldErrors}
                setErrors={setFieldErrors}
                fieldPrefix="physical_address."
              />
            </Grid>
          )}

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Email Address"
              name="email_address"
              type="email"
              value={formValues.email_address}
              onChange={handleInputChange}
              error={Boolean(emailExists || emailError)}
              helperText={
                emailChecking
                  ? "Checking email..."
                  : emailError
                  ? emailError
                  : emailExists
                  ? "This email already exists."
                  : ""
              }
            />
            {/* Email Exists Dialog */}
            <Dialog
              open={emailDialogOpen}
              onClose={(_, reason) => {
                if (reason !== 'backdropClick') handleDialogClose();
              }}
            >
              <DialogTitle>Email Already Exists</DialogTitle>
              <DialogContent>
                <DialogContentText>
                  This email already exists. Would you like to continue with this email?
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button onClick={handleDialogCancel} color="secondary">Cancel</Button>
                <Button onClick={handleDialogContinue} color="primary" autoFocus>
                  Continue
                </Button>
              </DialogActions>
            </Dialog>
          </Grid>
          <Grid item xs={12}>
            <InputMask
              mask="999-999-9999"
              value={formValues.cell_phone}
              onChange={handleInputChange}
            >
              {(inputProps) => (
                <TextField
                  {...inputProps}
                  fullWidth
                  label="Cell Phone"
                  name="cell_phone"
                  placeholder="123-456-7890"
                />
              )}
            </InputMask>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Date of Birth"
              name="date_of_birth"
              type="date"
              value={formValues.date_of_birth}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Date of First Bond"
              name="date_of_first_bond"
              type="date"
              value={formValues.date_of_first_bond}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="OPPG Number"
              name="oppg_number"
              value={formValues.oppg_number}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="AMS Customer Number"
              name="ams_customer_number"
              value={formValues.ams_customer_number}
              onChange={handleInputChange}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Fiduciary Questionnaire Completion Date"
              name="professional_fiduciary_questionnaire_last_completion_date"
              type="date"
              value={formValues.professional_fiduciary_questionnaire_last_completion_date}
              onChange={handleInputChange}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          {/* Total Bonds */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Total Bonds"
              name="total_bonds"
              type="number"
              value={formValues.total_bonds}
              onChange={handleInputChange}
            />
          </Grid>

          {/* Total Outstanding Bond Amount */}
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Total Outstanding Bond Amount"
              name="total_outstanding_bond_amount"
              type="number"
              value={formValues.total_outstanding_bond_amount}
              onChange={handleInputChange}
            />
          </Grid>
        </Grid>

        {/* Boolean Fields */}
        <Grid container spacing={2} sx={{ marginTop: 2 }}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.permission_to_text}
                  onChange={handleInputChange}
                  name="permission_to_text"
                />
              }
              label="Permission to Text"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.general_indemnity_agreement_on_file}
                  onChange={handleInputChange}
                  name="general_indemnity_agreement_on_file"
                />
              }
              label="General Indemnity Agreement on File"
            />
          </Grid>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formValues.letter_of_authority}
                  onChange={handleInputChange}
                  name="letter_of_authority"
                />
              }
              label="Letter of Authority"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              select
              label="Preferred Billing Method"
              name="preferred_billing"
              fullWidth
              required
              value={
                formValues.preferred_billing === true
                  ? "true"
                  : formValues.preferred_billing === false
                  ? "false"
                  : ""
              }
              onChange={(e) =>
                setFormValues({
                  ...formValues,
                  preferred_billing:
                    e.target.value === "true"
                      ? true
                      : e.target.value === "false"
                      ? false
                      : null,
                })
              }
            >
              <MenuItem value="true">Yes</MenuItem>
              <MenuItem value="false">No</MenuItem>
            </TextField>
          </Grid>
        </Grid>

        {/* PG Contacts Section */}
        <Box sx={{ marginTop: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Professional Guardian Contacts</Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {showResetButton && (
                <Button
                  variant="outlined"
                  startIcon={<RefreshIcon />}
                  onClick={() => setResetDialogOpen(true)}
                  color="warning"
                >
                  Reset All Data
                </Button>
              )}
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openContactDialog}
              >
                Add Contact
              </Button>
            </Box>
          </Box>

          {/* Local Contacts (before creation) */}
          {localContacts.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1, color: 'primary.main', fontWeight: 'bold' }}>
                Pending Contacts ({localContacts.length})
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone Number</TableCell>
                      
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {localContacts.map((contact, index) => (
                      <TableRow key={`local-${index}`}>
                        <TableCell>{contact.full_name}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>{contact.phone_number}</TableCell>
                        
                        <TableCell>
                          <Chip 
                            label="Pending" 
                            color="warning" 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleEditContact(index)}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteContact(contact, index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {/* Existing PG Contacts (after creation) */}
          {pgContacts.length > 0 && (
            <>
              <Typography variant="subtitle1" sx={{ mb: 1, color: 'success.main', fontWeight: 'bold' }}>
                Created Contacts ({pgContacts.length})
              </Typography>
              <TableContainer component={Paper} sx={{ mb: 2 }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Full Name</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone Number</TableCell>
                      
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pgContacts.map((contact, index) => (
                      <TableRow key={`pg-${index}`}>
                        <TableCell>{contact.full_name}</TableCell>
                        <TableCell>{contact.email}</TableCell>
                        <TableCell>{contact.phone_number}</TableCell>
                        
                        <TableCell>
                          <Chip 
                            label="Created" 
                            color="success" 
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            onClick={() => handleEditContact(localContacts.length + index)}
                            disabled={contact.user}
                          >
                            <EditIcon />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteContact(contact, localContacts.length + index)}
                          >
                            <DeleteIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}

          {localContacts.length === 0 && pgContacts.length === 0 && (
            <Alert severity="info">
              No contacts added yet. Click "Add Contact" to add professional guardian contacts.
            </Alert>
          )}

          {isCreatingContacts && (
            <Alert severity="info" sx={{ mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} />
                Creating contacts...
              </Box>
            </Alert>
          )}
        </Box>

        {/* Send Invite Checkbox */}
        <Box sx={{ marginTop: 3 }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendInvite}
                onChange={(e) => setSendInvite(e.target.checked)}
              />
            }
            label="Send Invite Email"
          />
        </Box>

        {/* Submit Button */}
        <Box sx={{ marginTop: 2 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading || isCreatingContacts}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : isCreatingContacts ? (
              <>
                <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                Creating Guardian & Contacts...
              </>
            ) : (
              "Create Guardian"
            )}
          </Button>
        </Box>
        </Paper>
      </Container>

      {/* Contact Dialog */}
      <Dialog
        open={contactDialogOpen}
        onClose={() => setContactDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {editingContactIndex !== null ? "Edit Contact" : "Add Contact"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Full Name"
                name="full_name"
                value={contactForm.full_name}
                onChange={handleContactInputChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={contactForm.email}
                onChange={handleContactInputChange}
                error={Boolean(contactEmailExists || contactEmailError)}
                helperText={
                  contactEmailChecking
                    ? "Checking email..."
                    : contactEmailError
                    ? contactEmailError
                    : contactEmailExists
                    ? "This email already exists."
                    : ""
                }
                required
              />
            </Grid>
            <Grid item xs={12}>
              <InputMask
                mask="+1-999-999-9999"
                value={contactForm.phone_number}
                onChange={handleContactInputChange}
              >
                {(inputProps) => (
                  <TextField
                    {...inputProps}
                    fullWidth
                    label="Phone Number"
                    name="phone_number"
                    placeholder="+1-555-123-4567"
                  />
                )}
              </InputMask>
            </Grid>
            
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setContactDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleAddContact}
            variant="contained"
            disabled={!contactForm.full_name || !contactForm.email || contactEmailError || contactEmailChecking}
          >
            {editingContactIndex !== null ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'error.main' }}>
          Delete Contact
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete <strong>{contactToDelete?.contact?.full_name}</strong>? 
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setDeleteDialogOpen(false)}
            color="primary"
            disabled={deletingContact === contactToDelete?.contact?.contact_id}
          >
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteContact}
            variant="contained"
            color="error"
            disabled={deletingContact === contactToDelete?.contact?.contact_id}
          >
            {deletingContact === contactToDelete?.contact?.contact_id ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Delete"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reset All Data Confirmation Dialog */}
      <Dialog
        open={resetDialogOpen}
        onClose={() => setResetDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: 'warning.main' }}>
          Reset All Data
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to reset all data? This will clear:
            <ul>
              <li>All professional guardian form fields</li>
              <li>All pending contacts</li>
              <li>All created contacts</li>
              <li>Any invite information</li>
            </ul>
            This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setResetDialogOpen(false)}
            color="primary"
          >
            Cancel
          </Button>
          <Button 
            onClick={() => {
              resetAllData();
              setResetDialogOpen(false);
            }}
            variant="contained"
            color="warning"
          >
            Reset All Data
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CreateProfessionalGuardian;